<script setup name="TvbLayout">
import { inject, computed, provide, ref, watch } from 'vue'
import { TvbHeader, TvbFooter } from '@tvb-sz/tvb-headerfooter-frontend'
import { useRoute, useRouter } from 'vue-router'
import { useWindowSize } from '@vueuse/core'
import { websites, adsConfig } from './../constants/index'
import TvbBackTop from './../components/TvbBackTop/index.vue'
// import { useSeoMeta } from '@unhead/vue'
import { action } from './../utils/gtm'
import { Head } from '@unhead/vue/components'

provide('seoTitleSuffix', ' | TVB 無綫集團')
const size = inject('size')
const seo = inject('seo')
const adParams = ref({})
const route = useRoute()
const router = useRouter()

provide('setAdParams', (params = {}) => {
  adParams.value = params
})
// 路由变化时重置广告参数，防止新页面 wsky 广告读到上一页的 stale params
watch(
  () => route.fullPath,
  () => {
    adParams.value = {}
  }
)
const preview = computed(() => route?.meta?.preview)
provide('isPreview', preview)

// Sky Scraper 广告相关：判断当前是否为分类页面
const isCategoryPage = computed(() => route?.name === 'category')
const isVideoCategory = computed(() => {
  const categoryPath = route?.params?.categoryPath || ''
  return categoryPath.endsWith('-v')
})
// 判断当前是否为子分类页面（通过 adParams 中的 sub_category_path 判断）
const isSubCategory = computed(() => !!adParams.value?.sub_category_path)
// Sky Scraper 广告的 position：Article(7主/8子), Video(12主/13子)
const wskyPosition = computed(() => {
  if (isVideoCategory.value) return isSubCategory.value ? 13 : 12
  return isSubCategory.value ? 8 : 7
})
// 响应式窗口宽度，用于判断 Sky Scraper 显示条件（≥1576px）
const { width: windowWidth } = useWindowSize()
// adParams 就绪判断：分类页需等待 main_category_path(主分类) 或 sub_category_path(子分类) 注入
// 避免 wsky/splash 在 adParams 被路由切换重置为空时请求广告，导致 cust_params 缺失或残留上一页参数（修复 TC-1606）
const adParamsReady = computed(
  () => !!adParams.value?.main_category_path || !!adParams.value?.sub_category_path
)
const showSkyScraper = computed(
  () => isCategoryPage.value && windowWidth.value >= 1576 && adParamsReady.value
)

// Splash 广告的 position，使用 adsConfig 确保每种页面类型都有正确的 position
// 视频分类页需使用 position 12（videoCategory），与同页面其他广告一致（修复 TC-1627）
const splashPosition = computed(() => {
  if (isVideoCategory.value && route.name === 'category') return 12
  return adsConfig[route.name]?.position || 7
})

// Splash 广告的 unit 后缀
const splashUnit = computed(() => {
  if (isVideoCategory.value) return '/video'
  return adsConfig[route.name]?.unit || '/home'
})

// Splash 广告渲染时机：
// - 文章详情页：等待 Article 组件异步调用 setAdParams 注入 article_id 后再渲染，
//   确保 cust_params 携带 video_id（兼容含视频与不含视频的文章）
// - 视频详情页：等待 Video Detail 组件注入 video_id 后再渲染，确保 cust_params
//   携带 video_id 与 content_type=video（修复 TC-1607）
// - 分类页面：等待 adParams 注入 main_category_path 或 sub_category_path 后再渲染（修复 TC-1606）
// - 其他页面：立即渲染（保持原行为）
const showSplash = computed(() => {
  if (!route.name) return false
  if (route.name === 'detail') {
    return !!adParams.value?.article_id
  }
  // 视频详情页（含预览）：等待 video_id 注入后再渲染 splash
  if (['videoDetail', 'videoPreview'].includes(route.name)) {
    return !!adParams.value?.video_id
  }
  // 分类页面：等待 adParams 注入 main_category_path 或 sub_category_path 后再渲染（修复 TC-1606）
  if (route.name === 'category') {
    return adParamsReady.value
  }
  return true
})

// Splash 广告 key：视频详情页纳入 video_id，使切换剧集时 splash 重建刷新广告
// 其他页面保持原行为（按 route.fullPath 重建），避免影响文章详情页等
const splashKey = computed(() => {
  const base = `${route.fullPath}-${splashPosition.value}`
  if (['videoDetail', 'videoPreview'].includes(route.name)) {
    return `splash-${base}-${adParams.value?.video_id || ''}`
  }
  return `splash-${base}`
})

const bitClick = (e) => {
  switch (e.event_label) {
    case 'main-menu':
      action({
        page_title: 'main_menu',
        screen_class: e.title,
        screenID: 'S001',
        eventCategory: '',
        eventLabel: 'main_menu',
        eventID: e.id || 'B016',
        screenLabel: e.jump_url,
        ...(route.meta.layerData || {})
      })
      if (e.jump_url) {
        try {
          const url = new URL(e.jump_url)
          if (websites.includes(url.origin)) {
            e.event.preventDefault()
            router.push(`${url.pathname}${url.search}`)
          }
        } catch (error) {
          console.error(error)
        }
      }
      break
    case 'main-sub-menu':
      action({
        page_title: 'main_menu',
        screen_class: e.title,
        screenID: 'S001',
        eventCategory: '',
        eventLabel: 'main_menu',
        eventID: e.id || 'B016',
        screenLabel: e.jump_url,
        ...(route.meta.layerData || {})
      })
      if (e.jump_url) {
        try {
          const url = new URL(e.jump_url)
          if (websites.includes(url.origin)) {
            e.event.preventDefault()
            router.push(`${url.pathname}${url.search}`)
          }
        } catch (error) {
          console.error(error)
        }
      }
      break
    case 'logo':
      e.event.preventDefault()
      router.push('/')
      break
    case 'search-tag':
      e.event.preventDefault()
      if (e.id) {
        router.push(`/search?tag=${e.id}&tag_name=${e.name}`)
        action({
          page_title: 'search',
          search_term: e.name,
          resID: 'search',
          screenID: 'S001',
          eventCategory: '',
          eventLabel: 'search',
          eventID: 'B019',
          type: 'manual_search',
          screenLabel: `${location.origin}/search?tag=${e.id}`,
          page_referrer: `${location.origin}/search?tag=${e.id}`,
          ...(route.meta.layerData || {})
        })
      }
      break
    case 'search-keyword':
      e.event.preventDefault()
      if (e.keyword) {
        router.push(`/search?keyword=${e.keyword}`)
        action({
          page_title: 'search',
          search_term: e.keyword,
          resID: 'search',
          screenID: 'S001',
          eventCategory: '',
          eventLabel: 'search',
          eventID: 'B019',
          type: 'manual_search',
          screenLabel: `${location.origin}/search?keyword=${e.keyword}`,
          page_referrer: `${location.origin}/search?keyword=${e.keyword}`,
          ...(route.meta.layerData || {})
        })
      }
      break
    case 'footer-menu':
      action({
        page_title: 'footer_menu',
        screen_class: e.title,
        screenID: 'S001',
        eventCategory: '',
        eventLabel: 'footer_menu',
        eventID: 'B018',
        screenLabel: e.jump_url,
        ...(route.meta.layerData || {})
      })
      if (e.jump_url) {
        try {
          const url = new URL(e.jump_url)
          if (websites.includes(url.origin)) {
            e.event.preventDefault()
            router.push(`${url.pathname}${url.search}`)
          }
        } catch (error) {
          console.error(error)
        }
      }
      break
    default:
      break
  }
}

const bitFooterClick = (e) => {
  action({
    page_title: 'footer_menu',
    screen_class: e.title,
    screenID: 'S001',
    eventCategory: '',
    eventLabel: e.event_label,
    eventID: 'B018',
    screenLabel: e.jump_url || ''
  })
  if (e.jump_url) {
    try {
      const url = new URL(e.jump_url)
      if (websites.includes(url.origin)) {
        e.event.preventDefault()
        router.push(`${url.pathname}${url.search}`)
      }
    } catch (error) {
      console.error(error)
    }
  }
}

// const SeoMeta = computed(() => {
//   const { og_image_url, og_image_alt_txt } = seo?.value || {}
//   return {
//     ogImage: `${og_image_url || ''}`,
//     ogImageAlt: `${og_image_alt_txt || ''}`,
//     twitterImage: `${og_image_url || ''}`,
//     robots: preview.value || route?.meta?.noRobots ? 'noindex' : 'max-image-preview:large'
//   }
// })
// useSeoMeta(SeoMeta)

// const position = computed(() => adsConfig?.[route?.name]?.position || '')

// const unit = computed(() => {
//   switch (route.name) {
//     case 'category':
//       return `/${route.params.categoryPath}`.replace('-c', '')
//     case 'detail':
//       return `/${route.params.articleId}`
//     case 'search':
//       return '/search'
//     default:
//       return ''
//   }
// })

const hidBackTop = computed(
  () =>
    ['detail', 'articlePreview', 'vote', 'votePreview'].includes(route.name) &&
    ['s', 'xs'].includes(size.value)
)

// 是否隐藏页头页尾（用于错误页面）
const hideLayout = computed(() => route?.meta?.hideLayout || false)

const layerData = computed(() => route?.meta?.layerData || {})
const shortHeadRoutes = ['search', 'category', 'detail', 'articlePreview', 'vote', 'votePreview']
provide('layerData', layerData)
</script>

<template>
  <Head>
    <title>{{ seo.seo_title }}</title>
    <meta name="author" content="TVB" />
    <meta name="publisher" content="TVB" />
    <meta name="format-detection" content="telephone=no" />
    <meta name="robots" content="max-image-preview:large" />
    <meta name="description" :content="seo.meta_description" />
    <meta property="og:locale" content="zh_HK" />
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://www.tvb.com/" />
    <meta property="og:title" :content="seo.seo_title" />
    <meta property="og:description" :content="seo.meta_description" />
    <meta property="og:site_name" :content="seo.seo_title" />
    <meta property="og:image" :content="seo.og_image_url" />
    <meta property="og:image:alt" :content="seo.og_image_alt_txt" />
    <meta property="fb:pages" content="24078698448" />
    <meta name="twitter:title" :content="seo.seo_title" />
    <meta name="twitter:image" :content="seo.og_image_url" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@tvbcom" />
    <meta name="twitter:url" content="https://www.tvb.com/" />
    <meta name="twitter:description" :content="seo.meta_description" />
  </Head>
  <div class="tvb-container" :class="[size, { 'no-layout': hideLayout }]">
    <header v-if="!hideLayout" class="tvb-home-header" :class="[size]">
      <tvb-header
        @click="bitClick"
        :full-path="$route.fullPath"
        :key="`${shortHeadRoutes.includes($route.name) ? '1280px' : 'null'}+${$route.name === 'search' ? $route.query.keyword : ''}`"
        :max-width="shortHeadRoutes.includes($route.name) ? '1280px' : '1920px'"
      />
    </header>
    <router-view class="tvb-main" :class="[size]" :key="$route.fullPath" />
    <footer v-if="!hideLayout" class="tvb-home-footer">
      <tvb-footer
        @click="bitFooterClick"
        :max-width="shortHeadRoutes.includes($route.name) ? '1280px' : '1760px'"
      />
    </footer>
    <tvb-back-top v-if="!hidBackTop && !hideLayout" />
    <!-- Sky Scraper 浮动侧边栏广告（仅分类页面宽屏 ≥1576px 显示） -->
    <!-- 使用 adParams 由各页面通过 setAdParams 注入正确的广告参数 -->
    <tvb-ads
      v-if="showSkyScraper"
      :key="`wsky1-${route.fullPath}-${wskyPosition}`"
      :position="wskyPosition"
      ad-type="wsky"
      :unit="`/${route?.params?.categoryPath || ''}`"
      :params="adParams"
    />
    <tvb-ads
      v-if="showSkyScraper"
      :key="`wsky2-${route.fullPath}-${wskyPosition}`"
      :position="wskyPosition"
      ad-type="wsky"
      :unit="`/${route?.params?.categoryPath || ''}`"
      :params="adParams"
    />
    <!-- Web Interstitial 全屏插页广告（全站展示，由 GAM 控制频次） -->
    <tvb-ads
      v-if="!hideLayout && showSplash"
      :key="splashKey"
      :position="splashPosition"
      ad-type="splash"
      :unit="route?.params?.categoryPath ? `/${route?.params?.categoryPath}` : splashUnit"
      :params="adParams"
    />
    <tvb-cookies />
  </div>
</template>

<style lang="scss">
.tvb-container {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  .tvb-home-header {
    // height: 64px;
    background: #ffffff;
    // box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08);
    position: sticky;
    width: 100%;
    top: 0;
    z-index: 999;

    &.s,
    &.xs {
      height: 86px;

      &:has(.is-search-page-mobile) {
        height: 44px;
      }
    }

    // box-sizing: unset;

    .tvb-header {
      @media (min-width: 1025px) {
        .tvb-header-container[style*='1280px'] {
          // box-sizing: unset;

          padding: 0;
        }
      }

      @media (max-width: 1024px) {
        .tvb-mobile-menu {
          min-height: 42px;
        }
      }
    }
  }

  .tvb-main {
    --tvb-main-left: 56px;
    margin: 0 auto;
    min-height: calc(
      var(--window-height, 100vh) - var(--header-height, 215px) - var(--footer-height, 64px)
    );
    // transition: all 500ms;
    max-width: calc(1760px + 56px * 2);
    padding: 8px 56px 100px;
    width: 100%;
    box-sizing: border-box;

    // &.s,
    // &.xs {
    //   min-height: calc(var(--window-height) - var(--header-height));
    // }
    &.xl {
      max-width: calc(1760px + 56px * 2);
      padding: 8px 56px 100px;

      @media (max-width: 1440px) {
        --tvb-main-left: 40px;
        padding: 8px 40px 80px;
      }
    }

    &.l {
      --tvb-main-left: 40px;
      max-width: calc(1760px + 40px * 2);
      padding: 8px 40px 80px;
    }

    &.m {
      --tvb-main-left: 32px;
      max-width: calc(1760px + 32px * 2);
      padding: 8px 32px 56px;
    }

    &.s {
      --tvb-main-left: 16px;
      max-width: calc(1760px + 16px * 2);
      padding: 8px 16px 32px;
    }

    &.xs {
      --tvb-main-left: 12px;
      max-width: calc(1760px + 12px * 2);
      padding: 8px 12px 32px;
    }
  }

  .tvb-home-footer {
    margin-top: auto;

    :deep(.tvb-footer) {
      box-sizing: unset;

      .tvb-footer-copyright-follow .tvb-footer-follows .tvb-footer-follow {
        display: inline-block;
      }
    }
  }

  // 隐藏页头页尾时的样式（用于错误页面）
  &.no-layout {
    .tvb-main {
      min-height: 100vh;
      max-width: 100%;
      padding: 0;
    }
  }
}
</style>
